import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue, getPlanExecutionStats } from "../../utils/utils";
import { Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const AAP_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const AAP_PROGRAM = "ArLnAxhykoz";

const DIMENSION_ORDER = [
  "Economic Development (ED)",
  "Social Development (SD)",
  "Implementation, Coordination, Monitoring And Evaluation",
  "Governance, Corruption And Public Accountability(GCPA)",
  "Environment, infrastructure and human settlement",
  "Emergency Planning And Covid-19 Response",
  "Total",
];

const Table_1 = ({ year, district, period, hideTableDis, assessmentStatus, assessmentStatus1, normalizedUserRole }) => {
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState({});
  const [pieChartData, setPieChartData] = useState({});

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getAnnualActionPlan();
  }, [selectedYear, district, period]);

  async function getAnnualActionPlan() {
    try {
      const currentYear = selectedYear;
      const years = [currentYear - 2, currentYear - 1, currentYear];
      const [yearTwoLess, yearOneLess, yearNow] = years;
      const tempAllData = [];

      for (const yr of years) {
        const result = await axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${AAP_PROGRAM}&filter=${AAP_YEAR_ATTRIBUTE}:EQ:${yr}&pageSize=5000`,
        );

        const resp = await axios.get(
          `/tracker/events?program=${AAP_PROGRAM}&orgUnit=${district}&startDate=${yr}-01-01&endDate=${yr}-12-31&pageSize=5000`,
        );

        if (result.data.instances.length > 0) {
          const aap = result.data.instances;
          const reports = resp.data.instances;

          const formattedPlans = aap.map((plan, idx) => ({
            index: idx,
            dd: getAttributeValue("Development Dimension", plan),
            date: plan.createdAt,
            trackedEntity: plan.trackedEntity,
          }));

          const counts = getPlanExecutionStats(formattedPlans, reports).map((item) => ({
            ...item,
            year: yr,
            percentage:
              item.planned > 0
                ? ((item.executed / item.planned) * 100).toFixed(1)
                : 0,
          }));

          tempAllData.push(...counts);
        }
      }

      const grouped = {};
      const cleanedData = tempAllData.filter(
        (item) =>
          item.dimension &&
          item.dimension !== "Total" &&
          item.dimension !== "N/A",
      );

      cleanedData.forEach((item) => {
        const { dimension, planned, executed, year: itemYear } = item;

        if (!grouped[dimension]) {
          grouped[dimension] = {
            dimension,
            yearTwoLessPlan: 0,
            yearTwoLessExecuted: 0,
            yearOneLessPlan: 0,
            yearOneLessExecuted: 0,
            yearPlan: 0,
            yearExecuted: 0,
          };
        }

        if (itemYear === yearTwoLess) {
          grouped[dimension].yearTwoLessPlan += planned;
          grouped[dimension].yearTwoLessExecuted += executed;
        }

        if (itemYear === yearOneLess) {
          grouped[dimension].yearOneLessPlan += planned;
          grouped[dimension].yearOneLessExecuted += executed;
        }

        if (itemYear === yearNow) {
          grouped[dimension].yearPlan += planned;
          grouped[dimension].yearExecuted += executed;
        }
      });

      let formattedData = Object.values(grouped);

      const total = {
        dimension: "Total",
        yearTwoLessPlan: 0,
        yearTwoLessExecuted: 0,
        yearOneLessPlan: 0,
        yearOneLessExecuted: 0,
        yearPlan: 0,
        yearExecuted: 0,
      };

      formattedData.forEach((row) => {
        total.yearTwoLessPlan += row.yearTwoLessPlan;
        total.yearTwoLessExecuted += row.yearTwoLessExecuted;
        total.yearOneLessPlan += row.yearOneLessPlan;
        total.yearOneLessExecuted += row.yearOneLessExecuted;
        total.yearPlan += row.yearPlan;
        total.yearExecuted += row.yearExecuted;
      });

      formattedData = formattedData.filter((row) => row.dimension !== "Total");
      formattedData.push(total);

      formattedData.sort(
        (a, b) => DIMENSION_ORDER.indexOf(a.dimension) - DIMENSION_ORDER.indexOf(b.dimension),
      );

      setTableData(formattedData);

      const chartRows = formattedData.filter((row) => row.dimension !== "Total");

      setChartData({
        labels: chartRows.map((row) =>
          row.dimension.length > 25
            ? `${row.dimension.substring(0, 25)}...`
            : row.dimension,
        ),
        datasets: [
          {
            label: `${yearTwoLess} Planned`,
            data: chartRows.map((row) => row.yearTwoLessPlan),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
          },
          {
            label: `${yearTwoLess} Executed`,
            data: chartRows.map((row) => row.yearTwoLessExecuted),
            backgroundColor: "rgba(54, 162, 235, 1)",
          },
          {
            label: `${yearOneLess} Planned`,
            data: chartRows.map((row) => row.yearOneLessPlan),
            backgroundColor: "rgba(255, 206, 86, 0.6)",
          },
          {
            label: `${yearOneLess} Executed`,
            data: chartRows.map((row) => row.yearOneLessExecuted),
            backgroundColor: "rgba(255, 206, 86, 1)",
          },
          {
            label: `${yearNow} Planned`,
            data: chartRows.map((row) => row.yearPlan),
            backgroundColor: "rgba(75, 192, 192, 0.6)",
          },
          {
            label: `${yearNow} Executed`,
            data: chartRows.map((row) => row.yearExecuted),
            backgroundColor: "rgba(75, 192, 192, 1)",
          },
        ],
      });

      setPieChartData({
        labels: ["Implemented", "Non-Implemented"],
        datasets: [
          {
            data: [
              total.yearExecuted || 0,
              (total.yearPlan || 0) - (total.yearExecuted || 0),
            ],
            backgroundColor: [
              "rgba(75, 192, 192, 0.7)",
              "rgba(255, 99, 132, 0.7)",
            ],
          },
        ],
      });
    } catch (err) {
      console.error("Error in getAnnualActionPlan:", err);
      setTableData([]);
      setChartData({});
      setPieChartData({});
    }
  }

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Performance Comparison by Development Dimension (${selectedYear - 2}-${selectedYear})`,
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 30,
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: `Projects Implemented vs Non-Implemented (${selectedYear})`,
      },
    },
  };

  const dynamicParagraph = () => {
    if (tableData.length === 0) return null;

    const dimensionsData = tableData.filter((row) => row.dimension !== "Total");

    return (
      <p className="mt-3">
        <br />
        {dimensionsData.map((row) => (
          <span key={row.dimension}>
            {row.dimension === dimensionsData[dimensionsData.length - 1]?.dimension ? "" : <br />}
          </span>
        ))}
      </p>
    );
  };

  return (
    <div className="col-12">
      <h3>Table 1 – Proportion of the AAP Implemented by Development Dimensions</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
         
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
             hideTableDis={hideTableDis}
             assessmentStatus1={assessmentStatus1}
            
            
          />
          <div className="table-responsive">
            <table className="table table-bordered" style={{
              border: '1px solid #000',
              borderCollapse: 'collapse',
              width: '100%',
              marginTop: "20px"
            }}>
              <thead>
                <tr style={{ backgroundColor: '#d4edda', fontWeight: 'bold', border: '1px solid #000' }}>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>S/N</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Development Dimension</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">{selectedYear - 2}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">{selectedYear - 1}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">{selectedYear}</th>
                </tr>
                <tr style={{ fontWeight: 'bold', border: '1px solid #000' }}>
                  <th style={{ border: '1px solid #000' }}></th>
                  <th style={{ border: '1px solid #000' }}></th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                      <tr key={index} style={
                        row.dimension === "Total"
                          ? { fontWeight: 'bold' }
                          : {}
                      }>
                        <td style={{ border: '1px solid #000', fontWeight: 'bold' }}>{index + 1}</td>
                        <td style={{ border: '1px solid #000' }}>{row.dimension}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearTwoLessPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearTwoLessExecuted}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearOneLessPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearOneLessExecuted}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearExecuted}</td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {dynamicParagraph()}
          <hr />
          <h5>Performance Comparison by Development Dimension ({selectedYear - 2}-{selectedYear})</h5>
          {/* <div className="mt-10" style={{ height: "700px", margin: "0 auto" }}> */}
            {Object.keys(chartData).length > 0 ? (
              <div className="apr-chart-wrap">
                <Bar data={chartData} options={barChartOptions} />
              </div>
            ) : (
              <p>Loading bar chart data...</p>
            )}
          {/* </div> */}
          <hr />
          <h5>Projects Implemented vs Non-Implemented ({selectedYear})</h5>
          <div className="apr-chart-wrap apr-chart-wrap--pie w-full max-w-full">
            {Object.keys(pieChartData).length > 0 ? (
              <div style={{ width: "100%", maxWidth: 420, height: "min(55vh, 400px)", position: "relative" }}>
                <Pie data={pieChartData} options={pieChartOptions} />
              </div>
            ) : (
              <p>Loading pie chart data...</p>
            )}
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
              hideTableDis={hideTableDis}
             assessmentStatus ={assessmentStatus}
              assessmentStatus1={assessmentStatus1}
               normalizedUserRole ={normalizedUserRole}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
};

export default Table_1;